import { isEnvMode } from '@/utils/is-env';
import type { Language } from '@hero/schema';
import { CarrierAPI } from '@hero/sdk-carrier';
import { registerSessionAccessTokenReader } from '@hero/api-hooks';
import { createBrowserLocationCache, getDeviceTimezone } from '@hero/core';
import {
  createWebRefreshHandler,
  getAccessToken,
  getTempToken,
  registerRefreshHandler,
  registerProactiveRefreshRunner,
  runRegisteredRefresh,
  subscribeToAccessToken,
} from '@hero/web-hooks';

const baseURL = import.meta.env.VITE_API_BASE_URL;

// One shared cache per app instance — the SDK's `infoAdapter` runs
// on every request, and hitting `navigator.geolocation.getCurrentPosition`
// that often would tank latency and pop the OS prompt. The cache
// serves fresh (< 60s) snapshots or `null`; the backend's
// `PresenceInterceptor` uses the paired `capturedAt` + `accuracyMeters`
// headers to decide whether to fan the coord out to
// `User.meta.location.accurate` / Driver / Vehicle.
const locationCache = createBrowserLocationCache();

// Same refresh handler drives the SDK's 401 interceptor AND the
// boot-time silent refresh via `useAuthBootstrap`. Registering it in
// the module scope means the auth hooks can invoke it before any
// component has issued its first API call, which is what prevents
// the "logged out on hard reload" flash.
const refreshHandler = createWebRefreshHandler(baseURL);
registerRefreshHandler(refreshHandler);
registerProactiveRefreshRunner(refreshHandler);
registerSessionAccessTokenReader({
  getAccessToken,
  subscribe: subscribeToAccessToken,
});

const carrierAPI = new CarrierAPI({
  baseURL,
  // Unified web app at app.tryhero.io — every network party
  // (carrier, broker, customer, driver, individual customer) reports
  // as `dashboard`. See `.cursorrules` "Single web dashboard direction".
  app: 'dashboard',
  platform: 'web',
  enableLogs: isEnvMode(['development', 'staging']),
  // Auth-v2: send the HttpOnly `hero_rt` refresh cookie with every
  // request so `/auth/refresh` can rotate it without any JS access.
  withCredentials: true,
  // Auth-v2: silent-refresh on 401 — the SDK calls into this hook
  // once per inflight request, waits for the new access token, then
  // retries the original request with the fresh Bearer.
  refreshHandler: () => runRegisteredRefresh(),
  infoAdapter: async () => {
    // New users carry the temp JWT until the unified `auth.signUp`
    // exchanges it for a real `accessToken`. The SDK is the only
    // consumer of this adapter, so falling back to `tempToken` here
    // is what wires Bearer auth on the new-user wizard's `signUp`
    // request — the backend's `AuthGuard` verifies either shape.
    const access_token = getAccessToken() ?? getTempToken();
    const location = locationCache.read();
    return {
      access_token,
      language: localStorage.getItem('language') as Language,
      currentUserLocation: location?.latLng ?? null,
      currentUserLocationCapturedAt: location?.capturedAt ?? null,
      currentUserLocationAccuracyMeters: location?.accuracyMeters ?? null,
      timezone: getDeviceTimezone(),
    };
  },
});

export { carrierAPI };
